import { ReactElement, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { gql, useMutation } from '@apollo/client'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import styled from 'styled-components'

import Heading from 'components/v2/Heading'
import FormLayout from 'features/App/components/FormLayout'
import { ForbiddenView } from 'features/App/components/ForbiddenView'
import { FEATURES, PERMISSIONS } from 'features/Roles/components/Permissions/constants'
import NewAddonService from 'features/Ancillaries/components/NewAddonService'
import { mapAddonServiceFormToQuery, mapAddonServiceQueryToForm } from 'resolvers/addonServices'
import { useLogin } from 'features/Login/hooks'
import { canWrite } from 'features/Users/utils'
import { IAddonServiceForm } from 'features/Ancillaries/types'
import { getDefaultCompanyForForm } from 'features/Login/utils'

const FormLayoutContent = styled(FormLayout.Content)`
  margin-bottom: 200px;
`

const messages = defineMessages({
  helmet: {
    id: 'ancillaries.services.new.helmet',
    defaultMessage: 'New Addon Service | Nexbus Admin'
  }
})

const CREATE_ADDON_SERVICE = gql`
  mutation AddServiceFeeAddonRule($addonService: AddServiceFeeAddonRuleInput!) {
    addServiceFeeAddonRule(input: $addonService)
  }
`

const NewAddonServiceView = (): ReactElement => {
  const intl = useIntl()
  const navigate = useNavigate()
  const login = useLogin()
  const [createAddonService, { loading, error }] = useMutation(CREATE_ADDON_SERVICE, {
    onCompleted: (): TSFixMe => navigate('/ancillaries/services')
  })

  const handleSubmit = (addonService: IAddonServiceForm): void => {
    createAddonService({
      variables: {
        addonService: mapAddonServiceFormToQuery(addonService)
      }
    })
  }

  useEffect((): TSFixMe => {
    document.title = intl.formatMessage(messages.helmet)
  }, [])

  if (!canWrite(FEATURES.ANCILLARIES, login.accessToken.policies)) {
    return <ForbiddenView feature={PERMISSIONS.COMPANY_INSURANCE_SETTINGS_READ} />
  }

  const initialAddonService = mapAddonServiceQueryToForm({
    company: getDefaultCompanyForForm(login.accessToken)
  })

  return (
    <div>
      <FormLayout.Header>
        <Heading.H3>
          <FormattedMessage id="ancillaries.services.new.title" defaultMessage="Create Addon Service" />
        </Heading.H3>
      </FormLayout.Header>
      <FormLayoutContent>
        <NewAddonService addonService={initialAddonService} onSubmit={handleSubmit} error={error} loading={loading} />
      </FormLayoutContent>
    </div>
  )
}

export default NewAddonServiceView
